import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useDebounce from "~/hooks/useDebounce";
import useSolrSearch from "~/hooks/useSolrSearch";
import { AdvancedSearchContext } from "~/providers/AdvancedSearchProvider";
import { useDisclosureStore, useHelperStore, useTabStore } from "~/stores";
import { SearchPanelMessageType } from "~/components/SearchPanelMessage";
import { SEARCH_VIEW_MODE } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";
import useDefaultAllDisclosures from "./useDefaultAllDisclosures";

const useKeywordSearch = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [isSearchEnabled, setIsSearchEnabled] = useState(false);
    const [showMsg, setShowMsg] = useState(false);
    const [searchMsg, setSearchMsg] = useState({
        msgType: SearchPanelMessageType.DEFAULT,
        message: "",
    });
    const dispatchHelper = useHelperStore(state => state.dispatch);
    const dispatch = useHelperStore(state => state.dispatch);
    const dispatchDisclosure = useDisclosureStore(state => state.dispatch);
    const inputRef = useRef<HTMLInputElement>(null);

    const noKeywordMsg = {
        msgType: SearchPanelMessageType.ERROR,
        message: t("searchForm.keywordSearchMsgs.noKeyword"),
    };
    const noResultMsg = {
        msgType: SearchPanelMessageType.INFO,
        message: t("searchForm.keywordSearchMsgs.noResult"),
    };

    // SolR Search implementation
    const SolRSearchType = `textsearch-${import.meta.env.VITE_REGION}`;
    const { fetchSearchResults } = useSolrSearch(SolRSearchType);

    // Store states
    const activeTab = useTabStore(state => state.tab);
    const disclosure = useDisclosureStore(state => state.disclosure);

    const { setSearchTerm, setActiveTabs } = useContext(AdvancedSearchContext);
    const { debouncedText, setText } = useDebounce({});
    const { DEFAULT_ALL_DISCLOSURES } = useDefaultAllDisclosures();
    const isAllDisclosure = useDisclosureStore(state => state.isAllDisclosuresAvailable);
    const navigate = useNavigate();

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        setText(event.target.value);

    const handleClearMessage = () => setShowMsg(false);
    
    const closeModal = (resetDisclosures = false) => {
        dispatchHelper({ type: SearchActions.SET_IS_MODAL_OPEN, payload: false });
        dispatchHelper({
            type: SearchActions.SET_SEARCH_VIEW_MODE,
            payload: SEARCH_VIEW_MODE.SELECTION
        });

        if (resetDisclosures && isAllDisclosure) {
            dispatchDisclosure({
                type: SearchActions.SET_ACTIVE_DISCLOSURE,
                payload: DEFAULT_ALL_DISCLOSURES
            });
        }
    };

    const handleSearch = () => {
        // Check keyword or search term is empty
        // If keyword is empty, display error message
        if (!debouncedText) {
            setSearchMsg(noKeywordMsg);
            setShowMsg(true);
            return;
        } else {
            // Navigate to search page
            setSearchTerm(debouncedText);
            setActiveTabs([activeTab?.key ?? "texts"]);
            closeModal(true);
            navigate("/search/result");
        }
    };
    
    const hidePanelMessage = () => {
        setIsSearchEnabled(true);
        setShowMsg(false);
        setIsLoading(false);
    };

    const showPanelMessage = () => {
        setIsSearchEnabled(false);
        setSearchMsg(noResultMsg);
        setShowMsg(true);
        setIsLoading(false);
    };

    const checkResults = async (keyword: string) => {
        if (!keyword) {
            setIsSearchEnabled(true);
            return;
        }

        try {
            setIsLoading(true);
            setIsSearchEnabled(false);

            const data = await fetchSearchResults(true, keyword);
            const hasResults = data.total > 0;
            hasResults ? hidePanelMessage() : showPanelMessage();
        } catch (error) {
            console.error(error);
            showPanelMessage();
        }
    };

    const handleSetShowDialog = (value: boolean) => {
        dispatchHelper({ type: SearchActions.SET_IS_MODAL_OPEN, payload: value });
        dispatchHelper({
            type: SearchActions.SET_SEARCH_VIEW_MODE,
            payload: SEARCH_VIEW_MODE.SELECTION
        });
    };

    const handleBtnClick = (open: boolean) => {
        const topicsViewMode = open ? SEARCH_VIEW_MODE.KEYWORD : SEARCH_VIEW_MODE.SELECTION;
        dispatch({ type: SearchActions.SET_SEARCH_VIEW_MODE, payload: topicsViewMode });

        if (!open && inputRef.current) {
            inputRef.current.value = "";
            setText("");
        }
    };

    useEffect(() => {
        checkResults(debouncedText);
    }, [debouncedText, disclosure]);

    return {
        isLoading,
        isSearchEnabled,
        searchMsg,
        keyword: debouncedText,
        showMsg,
        handleInputChange,
        handleSearch,
        handleClearMessage,
        handleSetShowDialog,
        closeModal,
        handleBtnClick,
        inputRef
    };
};

export default useKeywordSearch;
